import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Ticket, Send, X } from "lucide-react";

const PRIORITY_COLORS = {
  urgent: "text-red-600", high: "text-orange-600", medium: "text-amber-600", low: "text-slate-500",
};
const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-purple-100 text-purple-700",
  waiting_customer: "bg-amber-100 text-amber-700",
  resolved: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-700",
};

export default function TicketsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", priority: "medium" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => setRows((await api.get("/tickets")).data), []);
  useEffect(() => { load(); }, [load]);

  // Iter-109 · pre-fill from ?prefill_title=&prefill_body= (Guide → Ticket handoff)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get("prefill_title"); const b = p.get("prefill_body");
    if (t || b) {
      setForm((f) => ({ ...f, title: t || f.title, body: b || f.body }));
      setShowNew(true);
    }
  }, []);

  const create = async () => {
    if (!form.title || !form.body) return toast.error("Title + description required");
    setBusy(true);
    try {
      const r = await api.post("/tickets", form);
      toast.success(`Ticket ${r.data.number} created`);
      setShowNew(false);
      setForm({ title: "", body: "", priority: "medium" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="tickets-page">
      <PageHeader eyebrow="Support" title="My tickets"
        description="Raise a ticket about anything on Zenith 360 — bugs, questions, feature requests. Our team responds within the SLA."
        actions={
          <Button onClick={() => setShowNew(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="ticket-new">
            <Plus className="w-4 h-4" /> New ticket
          </Button>
        }
      />
      <Card className="rounded-sm">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/30">
            <tr>
              <th className="text-left p-3">Ticket</th>
              <th className="text-left p-3">Priority</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Last update</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((t) => (
              <tr key={t.id} onClick={() => setOpen(t)} className="cursor-pointer hover:bg-muted/30" data-testid={`user-ticket-row-${t.number}`}>
                <td className="p-3">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-[10px] font-mono text-muted-foreground">{t.number}</div>
                </td>
                <td className="p-3 text-[11px] font-mono uppercase"><span className={PRIORITY_COLORS[t.priority]}>{t.priority}</span></td>
                <td className="p-3"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm ${STATUS_COLORS[t.status]}`}>{t.status.replaceAll("_", " ")}</span></td>
                <td className="p-3 text-[11px] text-muted-foreground">{t.updated_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground italic">No tickets yet — hit "New ticket" to raise one</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowNew(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-sm shadow-2xl p-5 space-y-3" data-testid="ticket-new-modal">
            <div className="flex items-center justify-between">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-accent">Raise a ticket</div>
                <h2 className="text-lg font-bold">New support request</h2></div>
              <button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-muted-foreground" /></button>
            </div>
            <div><Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="One-line summary" className="mt-1" autoFocus data-testid="ticket-title" />
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1" data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent — blocking work</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low — nice to have</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label>
              <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5}
                className="mt-1 w-full border rounded-sm p-2 text-sm" placeholder="Steps to reproduce, expected vs actual, screenshots (paste image URL)…" data-testid="ticket-body" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button onClick={create} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="ticket-create">
                {busy ? "…" : "Create"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {open && <UserTicketDrawer ticket={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function UserTicketDrawer({ ticket, onClose, onChanged }) {
  const [t, setT] = useState(ticket);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const reload = async () => setT((await api.get(`/tickets/${ticket.id}`)).data);
  const post = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/comments`, { body: comment });
      setComment(""); await reload(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white h-full overflow-y-auto p-5" data-testid="user-ticket-drawer">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">{t.number}</div>
            <div className="text-xl font-bold">{t.title}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-muted-foreground" /></button>
        </div>
        <div className="text-[11px] font-mono text-muted-foreground mb-3">
          {t.priority?.toUpperCase()} · {t.status?.replaceAll("_", " ")} · Raised {t.created_at?.slice(0, 16).replace("T", " ")}
        </div>
        <div className="bg-muted/30 border rounded-sm p-3 text-sm whitespace-pre-wrap mb-4">{t.body}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">Conversation ({t.comments?.length || 0})</div>
        <div className="space-y-2 mb-4">
          {(t.comments || []).map((c) => (
            <div key={c.id} className={`rounded-sm p-3 border ${c.is_admin ? "bg-orange-50 border-orange-200" : "bg-slate-50 border-slate-200"}`}>
              <div className="text-[10px] font-mono text-muted-foreground mb-1">
                {c.is_admin ? "🛡 Support" : "You"} · {c.at?.slice(0, 16).replace("T", " ")}
              </div>
              <div className="text-sm whitespace-pre-wrap">{c.body}</div>
            </div>
          ))}
        </div>
        <div className="flex items-end gap-2 border-t pt-3">
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
            placeholder="Add a reply…" className="flex-1 border rounded-sm p-2 text-sm" data-testid="user-drawer-reply" />
          <Button onClick={post} disabled={busy || !comment.trim()} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="user-drawer-reply-send"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}
